import { D } from "@mobily/ts-belt";
import { addons } from "../../../../domain/addon";

/** Addon names that belong in the "Enhance Your Experience" section. */
export const EXPERIENCE_ADDON_NAMES = new Set([
  addons.VIDEO_ADDON,
  addons.VIDEO_MESSAGE_ADDON,
  addons.VIP_ADDON,
]);

/**
 * Split available addons into physical (Protect & Present) and experience
 * (Enhance Your Experience) categories.
 *
 * @param {object} availableAddons — addon map from useAvailableAddonsForProduct
 * @returns {{ physicalAddons: object[], experienceAddons: object[] }}
 */
export const categorizeAddons = (availableAddons) => {
  const all = Object.values(availableAddons);

  const physicalAddons = all
    .filter((a) => !EXPERIENCE_ADDON_NAMES.has(a.name))
    .sort((a, b) =>
      a.name === addons.COA_ADDON ? -1 : b.name === addons.COA_ADDON ? 1 : 0,
    );

  const experienceAddons = all.filter((a) =>
    EXPERIENCE_ADDON_NAMES.has(a.name),
  );

  return {
    physicalAddons,
    experienceAddons,
  };
};

/**
 * Returns true when the addon should be disabled due to shell case mutual
 * exclusivity (selecting one case disables the other).
 */
export const shouldDisable = (addon, selectedAddons) => {
  if (
    addon.name === addons.SOFT_SHELL_CASE &&
    selectedAddons[addons.HARD_SHELL_CASE]
  ) {
    return true;
  }
  if (
    addon.name === addons.HARD_SHELL_CASE &&
    selectedAddons[addons.SOFT_SHELL_CASE]
  ) {
    return true;
  }
  return false;
};

/**
 * Returns the disabled text for an addon.
 */
export const disabledTextFor = () => "Unavailable";

/**
 * Remove selections that are no longer in the available addons list.
 * Returns the cleaned map when orphans exist, or null when nothing changed.
 */
export const cleanOrphanAddons = (selectedAddons, availableAddons) => {
  const availableNames = new Set(
    Object.values(availableAddons).map((a) => a.name),
  );
  const orphans = Object.keys(selectedAddons).filter(
    (name) => !availableNames.has(name),
  );
  if (orphans.length === 0) return null;
  return orphans.reduce((acc, name) => D.deleteKey(acc, name), selectedAddons);
};

/**
 * Toggle an addon in the selected addons map.
 * Returns the new map with the addon added or removed.
 */
export const toggleAddon = (selectedAddons, addon) => {
  const has = Boolean(selectedAddons[addon.name]);
  return has
    ? D.deleteKey(selectedAddons, addon.name)
    : D.set(selectedAddons, addon.name, addon);
};
